import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import withLoader from '../../components/Loader/withLoader.js';

// Actions
import { actions as routingActions } from '../../ducks/routing.js';
import { actions as authActions } from '../../ducks/auth.js';
import { actions as appActions } from '../../ducks/app.js';
import { actions as customerActions } from '../../ducks/customer.js';

// Components
import AccountTransactionsLayout from './AccountTransactionsLayout';
import AccountTransactionsCard from './AccountTransactionsCard/AccountTransactionsCard';
import HelpCard from '../../components/HelpCard/HelpCard.js';
import TransactionsTable from '../../components/TransactionsTable/TransactionsTable';
import ReplaceContact from '../../components/Replace/ReplaceContact/ReplaceContact';
// Icons
import AccountIcon from '@material-ui/icons/AccountBalanceOutlined';
import InfoIcon from '@material-ui/icons/InfoOutlined';

import { getString } from '../../helpers/stringHelpers.js';
import { getLoadedStatus } from '../../helpers/loaderHelpers';
import { hasRole } from '../../helpers/authHelpers.js';
import analyticsApi from '../../api/analytics.js';
import constants from '../../constants';

const stringsComponent = 'accounts';

class AccountTransactions extends React.Component {
  componentDidMount() {
    const { setAppBarTitle, accountType } = this.props;

    setAppBarTitle(getString(stringsComponent, 'TRANSACTIONS_TITLE'));
  }

  shouldComponentUpdate(nextProps) {
    // Only rerender if not in a unloaded status.
    if (getLoadedStatus(this.getLoadedStatuses(nextProps)) !== 'UNLOADED') {
      return true;
    }
    return false;
  }

  getLoadedStatuses(props = this.props) {
    const { tenancy, account, transactions, appointments, accountType, roles } =
      props;
    const loaded = [
      tenancy.loaded,
      account.loaded,
      transactions[accountType].loaded,
    ];
    return loaded;
  }

  render() {
    const {
      signout,
      getTransactions,
      tenancy,
      account,
      transactions,
      inOutage,
      outageFrom,
      outageTo,
      outageMessage,
      accountType,
      goAccountDetails,
      customerName,
      toggleContactModal,
      onLoadComplete,
    } = this.props;

    const tenancyData = tenancy.data;
    const accountData = account.data;
    const transactionData = transactions[accountType].data;

    let gaDetails = '';
    if (accountType === 'maintenance') {
      gaDetails = 'Maintenance Account Statement';
    } else if (accountType === 'rent') {
      gaDetails = 'Rent Account Statement';
    }

    const help = portalGuidances.find(
      (guidance) => guidance.Title === stringsComponent + '_' + accountType
    ) && (
      <HelpCard
        title={getString('help', 'TITLE')}
        subheading={getString('help', 'SUBHEADING')}
        content={
          portalGuidances.find(
            (guidance) =>
              guidance.Title === stringsComponent + '_' + accountType
          ).Content
        }
        contact={
          portalGuidances.find(
            (guidance) =>
              guidance.Title === stringsComponent + '_' + accountType
          ).Contact
        }
        faqs={
          portalGuidances.find(
            (guidance) =>
              guidance.Title === stringsComponent + '_' + accountType
          ).FAQs
        }
        icon={<InfoIcon />}
        toggleContactModal={toggleContactModal}
        gaEvent={analyticsApi.event}
      />
    );

    const transactionsTable = (
      <TransactionsTable
        transactions={
          transactionData.hasOwnProperty('transactions')
            ? transactionData.transactions
            : []
        }
        currentBalance={
          transactionData.hasOwnProperty('closingBalance')
            ? Number(transactionData.closingBalance)
            : 0
        }
        openingBalance={
          transactionData.hasOwnProperty('openingBalance')
            ? Number(transactionData.openingBalance)
            : 0
        }
        customerName={customerName}
        address={
          tenancyData.hasOwnProperty('currentTenancy') &&
          tenancyData.currentTenancy.hasOwnProperty('propertyAddress')
            ? tenancyData.currentTenancy.propertyAddress
            : ''
        }
        tenantStatus={
          tenancyData.hasOwnProperty('currentTenancy') &&
          tenancyData.currentTenancy.hasOwnProperty('tenantStatus')
            ? tenancyData.currentTenancy.tenantStatus
            : ''
        }
        tenantStart={
          tenancyData.hasOwnProperty('currentTenancy') &&
          tenancyData.currentTenancy.hasOwnProperty('tenancyStart')
            ? tenancyData.currentTenancy.tenancyStart
            : ''
        }
        tenantEnd={
          tenancyData.hasOwnProperty('currentTenancy') &&
          tenancyData.currentTenancy.hasOwnProperty('tenancyEnd')
            ? tenancyData.currentTenancy.tenancyEnd
            : null
        }
        paymentReference={
          accountData.hasOwnProperty('accounts') &&
          accountData.accounts.hasOwnProperty(accountType)
            ? accountData.accounts[accountType].paymentReference
            : ''
        }
        accountType={accountType}
        toggleContactModal={toggleContactModal}
        gaEvent={() => analyticsApi.event('Accounts', 'Click', gaDetails)}
      />
    );

    const transactionsCard = (
      <AccountTransactionsCard
        title={getString(stringsComponent, 'TRANSACTIONS_TITLE')}
        body={transactionsTable}
        icon={<AccountIcon />}
      />
    );

    const WrappedAccountTransactionsLayout = withLoader(
      AccountTransactionsLayout
    );

    return (
      <WrappedAccountTransactionsLayout
        help={help}
        tabs={transactionsCard}
        backAction={() => goAccountDetails(accountType)}
        loaded={this.getLoadedStatuses()}
        onRefresh={getTransactions}
        onError={signout}
        inOutage={inOutage}
        outageFrom={outageFrom}
        outageTo={outageTo}
        outageMessage={
          outageMessage && (
            <ReplaceContact
              content={outageMessage}
              action={toggleContactModal}
              isLink={true}
            />
          )
        }
        failureText={
          <ReplaceContact
            content={portalContent.APP_ERROR_MESSAGE}
            action={toggleContactModal}
            isLink={true}
          />
        }
        notFoundText={portalContent.APP_NOT_FOUND_MESSAGE}
        onLoadComplete={onLoadComplete}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  tenancy: state.customer.tenancy,
  account: state.customer.account,
  transactions: state.customer.transactions,
  appointments: state.customer.appointments,
  inOutage: state.app.inOutage,
  outageFrom: state.app.outageFrom,
  outageTo: state.app.outageTo,
  outageMessage: state.app.outageMessage,
  accountType: state.app.accountType,
  customerName: `${state.auth.firstName} ${state.auth.lastName}`,
  roles: state.auth.roles,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...routingActions,
      ...appActions,
      ...authActions,
      ...customerActions,
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AccountTransactions);
